import { X } from 'lucide-react'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { ExpandingIconButton } from '@/components/ui/expanding-icon-button'
import { TableCell, TableRow } from '@/components/ui/table'
import {
  displayPipelineStageDate,
  lapseForecast,
  type StageKey,
} from '@/pages/RenewalPipelineListControls'
import type { PipelineAction, PipelineRow } from '@/pages/RenewalPipelinePage.types'
import { RenewalPipelineRowActions } from '@/pages/RenewalPipelineRowActions'
import { displayDate } from '@/lib/dates'
import { useViewOnly } from '@/components/templates/app-shell'

const statusVariant: Record<PipelineRow['status'], BadgeProps['variant']> = {
  Active: 'success',
  'Pending-Renewal': 'neutral',
  Lapsed: 'warning',
}

export function RenewalPipelineTableRow({
  row,
  stage,
  charging,
  dismissing,
  onAction,
  onChargeNow,
  onDismiss,
  onOpenTimeline,
  onOpenMember,
  onOpenOrganization,
}: {
  row: PipelineRow
  stage: StageKey
  charging: boolean
  dismissing: boolean
  onAction: (action: PipelineAction) => void
  onChargeNow: (invoiceId: number) => void
  onDismiss: (row: PipelineRow) => void
  onOpenTimeline: (row: PipelineRow) => void
  onOpenMember: (personId: number) => void
  onOpenOrganization?: (organizationId: number) => void
}) {
  const viewOnly = useViewOnly()

  return (
    <TableRow
      tabIndex={0}
      className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      onClick={() => onOpenTimeline(row)}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        onOpenTimeline(row)
      }}
    >
      <TableCell className="text-body-sm font-medium">
        <div>{row.name}</div>
        {row.scheduled_end_on && (
          <div className="text-caption font-normal text-muted-foreground">
            Cancels on {displayDate(row.scheduled_end_on)}
          </div>
        )}
        {stage === 'needs_decision' && (
          <div className="text-caption font-normal text-muted-foreground">{row.level}</div>
        )}
      </TableCell>
      <TableCell>
        <Badge variant={statusVariant[row.status]}>{row.status}</Badge>
      </TableCell>
      <PipelineStageCells row={row} stage={stage} />
      {stage !== 'needs_decision' && (
        <TableCell>
          <AutopayState value={row.autopay_state} />
        </TableCell>
      )}
      <TableCell className="py-1 text-right">
        <div className="flex items-center justify-end gap-1">
          {!viewOnly && stage === 'needs_decision' && row.dismissible && (
            <ExpandingIconButton
              icon={X}
              label={`Dismiss ${row.name}`}
              disabled={dismissing}
              onClick={(event) => {
                event.stopPropagation()
                onDismiss(row)
              }}
            />
          )}
          <RenewalPipelineRowActions
            row={row}
            charging={charging}
            onAction={onAction}
            onChargeNow={onChargeNow}
            onOpenMember={onOpenMember}
            onOpenOrganization={onOpenOrganization}
          />
        </div>
      </TableCell>
    </TableRow>
  )
}

/** A held row reads as the staff hold it is, with the note staff left beneath it. */
function DecisionReason({ row }: { row: PipelineRow }) {
  if (row.reason_kind !== 'hold' || !row.hold_ends_on) return row.reason
  return (
    <>
      <span className="block">Staff hold until {displayDate(row.hold_ends_on)}</span>
      {row.hold_reason && <span className="block text-caption">{row.hold_reason}</span>}
    </>
  )
}

function PipelineStageCells({ row, stage }: { row: PipelineRow; stage: StageKey }) {
  if (stage === 'needs_decision') {
    return (
      <>
        <TableCell className="text-body-sm text-muted-foreground">
          <DecisionReason row={row} />
        </TableCell>
        <TableCell className="text-right tabular-nums">
          {row.invoice_id ? row.open_amount : '—'}
        </TableCell>
        <TableCell className="text-body-sm">
          {row.hold_ends_on ? <Badge variant="neutral">Held</Badge> : lapseForecast(row)}
        </TableCell>
      </>
    )
  }

  return (
    <>
      <TableCell className="text-body-sm text-muted-foreground">{row.level}</TableCell>
      <TableCell className="text-right tabular-nums">
        <div>{row.invoice_id ? row.open_amount : '—'}</div>
        {row.paid_detail && (
          <div className="text-caption text-muted-foreground">{row.paid_detail}</div>
        )}
      </TableCell>
      <TableCell className="tabular-nums text-muted-foreground">
        {displayPipelineStageDate(row, stage)}
      </TableCell>
      <TableCell className="text-body-sm">{lapseForecast(row)}</TableCell>
    </>
  )
}

function AutopayState({ value }: { value: string }) {
  if (value.startsWith('Retry')) return <Badge variant="warning">{value}</Badge>
  if (value === 'Disabled') return <Badge variant="destructive">{value}</Badge>
  return <span className="text-body-sm text-muted-foreground">{value}</span>
}
